import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { api } from './api';
import Landing from './pages/Landing';
import Board from './pages/Board';
import SubmitRequest from './pages/SubmitRequest';
import TicketDetail from './pages/TicketDetail';
import Login from './pages/Login';
import ManageTrustees from './pages/ManageTrustees';
import Profile from './pages/Profile';

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const menuRef = useRef();
  const dropdownRef = useRef();
  const btnRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    try {
      const data = await api.me();
      setUser(data.authenticated ? data : null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Close menu on navigation
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      const inTrigger = menuRef.current && menuRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inTrigger && !inDropdown) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    try { await api.logout(); } catch { /* ignore */ }
    setUser(null);
    navigate('/');
  };

  if (loading) {
    return <div className="spinner" style={{ minHeight: '100vh' }} />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser, checkAuth }}>
      <header className="header">
        <div className="header-inner">
          {user ? (
            <>
              <Link to="/board" className="header-title">
                St. John's <span>Property Requests</span>
                <span className="header-title-badge">Dashboard</span>
              </Link>
              <nav className="header-nav">
                <Link to="/board" className={location.pathname === '/board' || location.pathname.startsWith('/ticket') ? 'active' : ''}>
                  Requests
                </Link>
                {user.isAdmin && (
                  <Link to="/trustees" className={location.pathname === '/trustees' ? 'active' : ''}>
                    Trustees
                  </Link>
                )}
                <div className="header-user" ref={menuRef}>
                  <button ref={btnRef} className="header-avatar-btn" onClick={() => {
                    if (!menuOpen && btnRef.current) {
                      const r = btnRef.current.getBoundingClientRect();
                      const menuWidth = 220; // matches CSS min-width
                      // Anchor right edge to button's right edge, but clamp so menu can't go off either side
                      const idealRight = window.innerWidth - r.right;
                      const maxRight = window.innerWidth - menuWidth - 12; // keep 12px from left edge
                      const right = Math.min(maxRight, Math.max(12, idealRight));
                      setMenuPos({ top: r.bottom + 8, right });
                    }
                    setMenuOpen(!menuOpen);
                  }}>
                    <div className="header-avatar">{user.name?.charAt(0).toUpperCase()}</div>
                    <span className="header-avatar-name">{user.name}</span>
                    <svg className={`header-avatar-chevron ${menuOpen ? 'open' : ''}`} width="10" height="10" viewBox="0 0 12 8" fill="none"><path d="M1 1.5l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              </nav>
            </>
          ) : (
            <>
              <Link to="/" className="header-title">
                St. John's <span>Property Requests</span>
              </Link>
              <nav className="header-nav">
                <Link to="/board" className={location.pathname === '/board' || location.pathname.startsWith('/ticket') ? 'active' : ''}>
                  Board
                </Link>
                <Link to="/submit" className={location.pathname === '/submit' ? 'active' : ''}>
                  Submit Request
                </Link>
                <Link to="/login" className={location.pathname === '/login' ? 'active' : ''}>
                  Trustee Login
                </Link>
              </nav>
            </>
          )}
        </div>
      </header>

      {menuOpen && createPortal(
        <div className="user-menu" ref={dropdownRef} style={{ top: menuPos.top, right: menuPos.right }}>
          <div className="user-menu-header">
            <span className="user-menu-name">{user.name}</span>
            <span className="user-menu-email">{user.isAdmin ? 'Admin' : 'Trustee'}</span>
          </div>
          <div className="user-menu-divider" />
          <button className="user-menu-item" onClick={() => { setMenuOpen(false); navigate('/profile'); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Profile
          </button>
          <button className="user-menu-item user-menu-item-danger" onClick={handleLogout}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Sign Out
          </button>
        </div>,
        document.body
      )}

      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/board" element={<Board />} />
        <Route path="/submit" element={<SubmitRequest />} />
        <Route path="/ticket/:id" element={<TicketDetail />} />
        <Route path="/login" element={<Login />} />
        {user?.isAdmin && <Route path="/trustees" element={<ManageTrustees />} />}
        {user && <Route path="/profile" element={<Profile />} />}
      </Routes>
    </AuthContext.Provider>
  );
}
